"use client";

import { NavigationBar } from "../navigationBar/navigationBar";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { CustomerProfileHeader } from "@/components/customerProfile/customerProfileHeader";
import { CheckoutHeader } from "@/components/checkout/checkoutHeader";
import { useState } from "react";
import { BusinessHoursModal } from "./hoursModal";
import { daysOfWeek } from "../merchantFooter/merchantFooter";
import { getTimezoneAbbreviation } from "@/utils/timeZoneAbbreviation";
import { formatTime } from "../merchantFooter/operatingHours";
import DownArrow from "./downArrow.svg";
import ClockIcon from "./clockIcon.svg";
import { CookiesBanner } from "./cookiesBanner";
import { Surcharge } from "../surcharge/surcharge";
import { VerifyEmailHeader } from "../verifyEmail/verifyEmailHeader";
import { NotAcceptingOrdersBanner } from "../notAcceptingOrdersBanner/notAcceptingOrdersBanner";
import MerchantLinkButton from "../merchantLinkButton/merchantLinkButton";

export interface HeaderProps {
  merchantLogo?: string;
  merchantName?: string;
  merchant: any;
  menu: any;
  merchantPreferences: any;
  deleteSession: any;
  customerDetails: any;
  merchantPaymentConfig?: any;
  retailCategories?: any;
  serviceCategories?: any;
  retailPickupTime: Object;
}

interface TimeSlot {
  start: string;
  end: string;
}

interface TimeSlotProps extends TimeSlot {
  abbreviation: string;
}

interface MainTimeSlotProps {
  todayHours: TimeSlot[];
  abbreviation: string;
}

const commonClasses = ["text-[#373737]", "items-right", "cursor-pointer"].join(
  " "
);

const TimeSlot = ({ start, end, abbreviation }: TimeSlotProps) => {
  return (
    <span className="whitespace-nowrap">
      {formatTime(start)} - {formatTime(end)} {abbreviation}
    </span>
  );
};

const MainTimeSlot = ({ todayHours, abbreviation }: MainTimeSlotProps) => {
  return (
    <div className={`${commonClasses} flex justify-end`}>
      <Image
        src={ClockIcon.src}
        height={14}
        width={14}
        style={{ marginTop: "5px" }}
        alt="clock icon"
      />
      <span className="font-medium px-1">Open</span>
      <TimeSlot
        start={todayHours[0].start}
        end={todayHours[0].end}
        abbreviation={abbreviation}
      />

      <Image
        src={DownArrow.src}
        height={20}
        width={20}
        alt="open business hours"
      />
    </div>
  );
};

const AdditionalTimeSlots = ({
  todayHours,
  abbreviation,
}: MainTimeSlotProps) => {
  return (
    <div className={`${commonClasses} pr-5`}>
      {todayHours.slice(1, 3).map((timeSlot: TimeSlot, index: number) => (
        <div key={index} className="whitespace-nowrap">
          <TimeSlot
            start={timeSlot.start}
            end={timeSlot.end}
            abbreviation={abbreviation}
          />
        </div>
      ))}
    </div>
  );
};

const MoreIndicator = () => {
  return (
    <div className={`${commonClasses} pr-5`}>
      <span className="ml-1 text-blue-600 hover:text-blue-800">...</span>
    </div>
  );
};

export const Header = ({
  merchantLogo = "https://placehold.co/1200x800?text=No+Image",
  merchantName = "Default Merchant Name",
  merchant,
  menu,
  merchantPreferences,
  deleteSession,
  customerDetails,
  merchantPaymentConfig,
  retailCategories,
  serviceCategories,
  retailPickupTime,
}: HeaderProps) => {
  const pathName = usePathname();
  const merchantColor = merchant.merchantMeta.colorPalette?.color;
  const isSimpleStorefront = merchant.merchantMeta.selectedSetup === "Simple";

  const [hoursModalIsOpen, setHoursModalIsOpen] = useState(false);
  const currentDay = new Date()
    .toLocaleDateString("en-US", { weekday: "long" })
    .toLowerCase();

  const service = merchant.services.find(
    (s: any) => s.type === "PICKUP" || s.type === "WEBSITE"
  );
  const headerBusinessHours = merchant.isRestaurant
    ? service?.hours
    : merchant?.businessHours?.hours;

  const handleHoursModal = () => {
    setHoursModalIsOpen(!hoursModalIsOpen);
  };
  const abbreviation = getTimezoneAbbreviation(
    merchant.timezone,
    merchant.locale
  );

  if (pathName == "/customer-profile") {
    return <CustomerProfileHeader />;
  }

  if (pathName == "/verify-email") {
    return <VerifyEmailHeader />;
  }

  if (pathName == "/checkout" || pathName == "/guest-checkout") {
    return <CheckoutHeader />;
  }
  const todayHours =
    headerBusinessHours !== undefined &&
    headerBusinessHours[currentDay] !== undefined
      ? headerBusinessHours[currentDay]
      : [];
  const hasHours = todayHours.length > 0;

  return (
    <nav
      className={[
        "flex",
        "flex-col",
        "items-center",
        "relative",
        "top-0",
        "z-10",
        "bg-[#FBFBFB]",
      ].join(" ")}
    >
      <CookiesBanner />
      <div
        className={[
          "flex",
          "flex-row",
          "px-5",
          "pt-5",
          "w-full",
          "justify-between",
        ].join(" ")}
      >
        <div
          className={["flex", "flex-row", "gap-6", "items-center"].join(" ")}
        >
          <Link href="/">
            <Image
              className={["h-[51px] w-[51px] sm:h-16 sm:w-16"].join(" ")}
              src={merchantLogo}
              alt="merchantLogo"
              width={100}
              height={100}
            />
          </Link>
          <Link href="/">
            <div className="flex flex-col">
              <p
                className={`text-2xl sm:text-[32px] leading-normal font-normal`}
              >
                {merchantName}
              </p>
              {isSimpleStorefront && (
                <div>
                  <p className="text-[15px]">
                    {merchant.address.address1 +
                      merchant.address.address2 +
                      ", " +
                      merchant.address.city +
                      ", " +
                      merchant.address.state +
                      " " +
                      merchant.address.zip}
                  </p>
                  <p className="text-[15px]">{merchant.phone}</p>
                </div>
              )}
            </div>
          </Link>
        </div>
        {isSimpleStorefront && (
          <div className="hidden sm:flex flex-row justify-center items-right">
            <div onClick={handleHoursModal} className="text-right">
              <div className={`${commonClasses} pb-4`}>
                {hasHours ? (
                  <>
                    <MainTimeSlot
                      todayHours={todayHours}
                      abbreviation={abbreviation}
                    />

                    {todayHours.length > 1 && (
                      <AdditionalTimeSlots
                        todayHours={todayHours}
                        abbreviation={abbreviation}
                      />
                    )}
                    {todayHours.length > 3 && <MoreIndicator />}
                  </>
                ) : (
                  <span className="font-medium px-1">Closed</span>
                )}
                {merchantPaymentConfig.surcharging.rate && (
                  <Surcharge
                    isTimeSlot={true}
                    surchargeRate={merchantPaymentConfig.surcharging.rate}
                  />
                )}
              </div>
            </div>
            <BusinessHoursModal
              isOpen={hoursModalIsOpen}
              onRequestClose={handleHoursModal}
              daysOfWeek={daysOfWeek}
              businessHours={headerBusinessHours}
              timezone={abbreviation}
              isRestaurantMerchant={merchant.isRestaurant}
            />
          </div>
        )}
        {pathName !== "/confirmation" &&
          !isSimpleStorefront &&
          !pathName?.startsWith("/services") && (
            <div className={["flex flex-col gap-6 items-center"].join(" ")}>
              <HeaderLinkButton
                merchant={merchant}
                merchantColor={merchantColor}
              />
              {/* need to check where to render surcharge for not simple strorefront? */}
            </div>
          )}
        {pathName === "/confirmation" && (
          <p className="hidden sm:block font-normal text-[15px]">
            <MerchantLinkButton
              merchantColor={merchantColor}
              uri="/"
              text="Keep browsing"
            />
          </p>
        )}
      </div>
      {pathName !== "/confirmation" && (
        <div className={["w-full"].join(" ")}>
          <NavigationBar
            merchant={merchant}
            menu={menu}
            customerPerks={customerDetails?.earnedPerks}
            merchantPreferences={merchantPreferences}
            deleteSession={deleteSession}
            retailCategories={retailCategories}
            serviceCategories={serviceCategories}
            retailPickupTime={retailPickupTime}
          />
        </div>
      )}
      <NotAcceptingOrdersBanner merchant={merchant} />
    </nav>
  );
};

const HeaderLinkButton = ({ merchant, merchantColor }: any) => {
  const pathName = usePathname();
  const isNotRestaurantOfferShown = !pathName
    .toLocaleLowerCase()
    .includes("restaurant");

  return (
    <>
      {merchant.isService && (
        <MerchantLinkButton
          merchantColor={merchantColor}
          uri="/services/all"
          text="Book Appointment"
        />
      )}
      {merchant.isRetail && (
        <MerchantLinkButton
          merchantColor={merchantColor}
          uri="/all"
          text="Shop All"
        />
      )}
      {(merchant.isRestaurant || merchant.isEssentialMerchant) &&
        (isNotRestaurantOfferShown || !merchant.isCheckoutEnabled) && (
          <MerchantLinkButton
            merchantColor={merchantColor}
            uri="/menu/all"
            text={`${
              merchant.isCheckoutEnabled ? "Order Online" : "View items"
            }`}
          />
        )}
    </>
  );
};
